/**
 * MemoryGrid molecule - the edge-to-edge photo grid of the Memories screen.
 *
 * iOS-Photos style: hairline 2px seams, no side padding, square cells (the
 * full frame lives in the viewer). Owns the 450ms long-press that enters
 * multi-select, cell accessibility, and the media badges on skin-invariant
 * camera-overlay tokens. Grid and cell are memoized — the page hands in
 * stable callbacks so search keystrokes don't repaint every tile.
 */
import { memo, useRef } from 'react';
import { frameDelta } from '../utils/deviceOrientation';
import { Check, Lock, Play } from 'lucide-react';
import { LazyThumbnail } from '../atoms/LazyThumbnail';
import type { SavedItem } from '../types';

export interface MemoryGridProps {
  items: SavedItem[];
  columnCount: 2 | 3 | 4 | 5;
  isSelecting: boolean;
  selectedIds: Set<number>;
  onItemClick: (item: SavedItem) => void;
  onItemLongPress: (item: SavedItem) => void;
}

const GRID_COLS: Record<number, string> = {
  2: 'grid-cols-2',
  3: 'grid-cols-3',
  4: 'grid-cols-4',
  5: 'grid-cols-5',
};

const LONG_PRESS_MS = 450;
const MOVE_CANCEL_PX = 10;

function cellLabel(item: SavedItem): string {
  const kind = item.media_type === 'video' ? 'Video' : 'Photo';
  const when = item.saved_at
    ? new Date(item.saved_at).toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      })
    : '';
  const caption = item.caption ? `, caption: ${item.caption}` : '';
  return `${kind} saved ${when}${caption}`;
}

const MemoryCell = memo(function MemoryCell({
  item,
  isSelecting,
  isSelected,
  onItemClick,
  onItemLongPress,
}: {
  item: SavedItem;
  isSelecting: boolean;
  isSelected: boolean;
  onItemClick: (item: SavedItem) => void;
  onItemLongPress: (item: SavedItem) => void;
}) {
  const pressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pressStart = useRef<{ x: number; y: number } | null>(null);
  const longPressFired = useRef(false);

  const clearPress = () => {
    if (pressTimer.current) {
      clearTimeout(pressTimer.current);
      pressTimer.current = null;
    }
    pressStart.current = null;
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length !== 1) {
      // Second finger = pinch, never a long-press
      clearPress();
      return;
    }
    longPressFired.current = false;
    pressStart.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
    pressTimer.current = setTimeout(() => {
      pressTimer.current = null;
      longPressFired.current = true;
      if ('vibrate' in navigator) navigator.vibrate(10);
      onItemLongPress(item);
    }, LONG_PRESS_MS);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!pressStart.current || e.touches.length !== 1) {
      clearPress();
      return;
    }
    const { dx, dy } = frameDelta(
      e.touches[0].clientX - pressStart.current.x,
      e.touches[0].clientY - pressStart.current.y,
    );
    if (Math.hypot(dx, dy) > MOVE_CANCEL_PX) clearPress();
  };

  const handleClick = () => {
    // The long-press already acted — swallow the click it releases into
    if (longPressFired.current) {
      longPressFired.current = false;
      return;
    }
    onItemClick(item);
  };

  return (
    <button
      onClick={handleClick}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={clearPress}
      onTouchCancel={clearPress}
      onContextMenu={(e) => e.preventDefault()}
      aria-label={cellLabel(item)}
      aria-pressed={isSelecting ? isSelected : undefined}
      className={`relative aspect-square overflow-hidden rounded-none bg-surface transition-transform active:scale-95 ${
        isSelected ? 'ring-2 ring-inset ring-primary' : ''
      }`}
    >
      <LazyThumbnail itemId={item.id} thumbhash={item.thumbhash} className="h-full w-full" />

      {/* Selection checkbox */}
      {isSelecting && (
        <div
          className={`absolute left-2 top-2 flex h-6 w-6 items-center justify-center rounded-full transition-colors ${
            isSelected
              ? 'bg-primary'
              : 'border-2 border-camera-overlay-text bg-camera-overlay-bg'
          }`}
        >
          {isSelected && <Check size={16} className="text-on-primary" />}
        </div>
      )}

      {/* Video badge */}
      {item.media_type === 'video' && !isSelecting && (
        <div className="absolute bottom-1 right-1">
          <Play size={20} fill="currentColor" className="text-camera-overlay-text drop-shadow" />
        </div>
      )}

      {/* Only Me badge */}
      {item.is_private && !isSelecting && (
        <div className="absolute left-1 top-1">
          <Lock size={16} className="text-camera-overlay-text drop-shadow" />
        </div>
      )}
    </button>
  );
});

const MemoryGrid = memo(function MemoryGrid({
  items,
  columnCount,
  isSelecting,
  selectedIds,
  onItemClick,
  onItemLongPress,
}: MemoryGridProps) {
  return (
    <div className={`grid gap-0.5 ${GRID_COLS[columnCount]}`}>
      {items.map((item) => (
        <MemoryCell
          key={item.id}
          item={item}
          isSelecting={isSelecting}
          isSelected={selectedIds.has(item.id)}
          onItemClick={onItemClick}
          onItemLongPress={onItemLongPress}
        />
      ))}
    </div>
  );
});

export default MemoryGrid;
